import React, { useMemo } from "react";
import {
  matchPath,
  useLocation,
  useParams,
  Link as RouterLink,
} from "react-router-dom";
import {
  Stack,
  Box,
  Breadcrumbs,
  Typography,
  Link as MuiLink,
} from "@mui/material";
import { CustomTruncateText } from "@ippon/shared-components";

// Type definitions
export interface BreadcrumbItem {
  path: string;
  title: string;
  icon?: React.ReactNode;
  clickable?: boolean; // 明示的にクリック可否を指定（デフォルトはtrue）
  truncatable?: boolean; // trueの場合、途中項目でmaxWidthで省略表示
}

export interface RouteEntry {
  icon?: React.ReactNode | ((params: BreadcrumbParams) => React.ReactNode);
  title?: string;
  breadcrumb:
    | BreadcrumbItem[]
    | ((params: BreadcrumbParams) => BreadcrumbItem[]);
}

export interface BreadcrumbParams {
  params?: Record<string, string | undefined>;
  propName?: string;
  customerName?: string;
  tabName?: string;
  employeeName?: string;
  [key: string]: any;
}

export interface BreadcrumbTheme {
  iconBackgroundColor?: string;
  iconBorderColor?: string;
  iconColor?: string;
  activeColor?: string;
  inactiveColor?: string;
  fontSize?: number;
  fontWeight?: string | number;
  linkDecoration?: string;
}

export interface BreadcrumbProps {
  // Route configuration
  routeConfig?: Record<string, RouteEntry>;

  // Dynamic data
  propName?: string;
  customerName?: string;
  tabName?: string;
  employeeName?: string;
  customParams?: Record<string, any>;

  // Customization
  theme?: BreadcrumbTheme;
  separator?: React.ReactNode;
  showIcon?: boolean;
  maxItems?: number;

  // Navigation
  location?: any; // Location from react-router-dom
  params?: Record<string, string | undefined>;
  onNavigate?: (path: string) => void;
  LinkComponent?: React.ComponentType<any>;

  // Custom styles
  containerStyle?: React.CSSProperties;
  iconStyle?: React.CSSProperties;

  // Callbacks
  onItemClick?: (item: BreadcrumbItem, index: number) => void;
}

// Default theme
const defaultTheme: Required<BreadcrumbTheme> = {
  iconBackgroundColor: "#fff",
  iconBorderColor: "#0e9dbc",
  iconColor: "#0e9dbc",
  activeColor: "#3E3E3E",
  inactiveColor: "#989898",
  fontSize: 12,
  fontWeight: "bold",
  linkDecoration: "none",
};

// Merge themes
const mergeTheme = (custom?: BreadcrumbTheme): Required<BreadcrumbTheme> => ({
  ...defaultTheme,
  ...custom,
});

// Default route config (can be overridden)
const defaultRouteConfig: Record<string, RouteEntry> = {
  "/dashboard": {
    breadcrumb: [{ path: "/dashboard", title: "Dashboard" }],
  },
  "/settings": {
    breadcrumb: [{ path: "/settings", title: "Settings" }],
  },
  "/profile": {
    breadcrumb: [{ path: "/profile", title: "Profile" }],
  },
};

export const Breadcrumb: React.FC<BreadcrumbProps> = ({
  routeConfig = defaultRouteConfig,
  propName,
  customerName,
  tabName,
  employeeName,
  customParams = {},
  theme,
  separator,
  showIcon = true,
  maxItems,
  location: propLocation,
  params: propParams,
  onNavigate,
  LinkComponent,
  containerStyle,
  iconStyle,
  onItemClick,
}) => {
  // Use provided location/params or get from hooks
  const hookLocation = useLocation();
  const hookParams = useParams();
  const location = propLocation || hookLocation;
  const params = propParams || hookParams;

  const mergedTheme = mergeTheme(theme);

  // Default Link component if none provided
  const Link = LinkComponent || RouterLink;

  // 1. Find the most specific matching route configuration
  //
  // Specificity の優先順位:
  //   1) パラメータ (`:foo`) が少ないパターンほど具体的 (リテラル一致を優先)
  //   2) 同点なら pattern 文字列が長い方を選ぶ (深い階層が具体的)
  //
  // これがないと例えば `/repairs/new` が `/repairs/:id` (id="new") に
  // 吸い込まれて、新規ページなのに「修繕ID: undefined」が出る等の不具合になる。
  const matchedRoute = useMemo(() => {
    let bestMatch = {
      pattern: "",
      route: null as RouteEntry | null,
      paramCount: Number.POSITIVE_INFINITY,
    };

    for (const [pattern, route] of Object.entries(routeConfig)) {
      const match = matchPath(pattern, location.pathname);
      if (!match) continue;
      const paramCount = (pattern.match(/:[^/]+/g) || []).length;
      const isMoreSpecific =
        paramCount < bestMatch.paramCount ||
        (paramCount === bestMatch.paramCount &&
          pattern.length > bestMatch.pattern.length);
      if (isMoreSpecific) {
        bestMatch = { pattern, route, paramCount };
      }
    }
    return bestMatch.route;
  }, [location.pathname, routeConfig]);

  // 2. Resolve breadcrumb items based on the matched route
  const breadcrumbItems = useMemo(() => {
    if (matchedRoute) {
      if (typeof matchedRoute.breadcrumb === "function") {
        const safeParams = Object.fromEntries(
          Object.entries(params).filter(([, v]) => v !== undefined)
        ) as Record<string, string>;

        return matchedRoute.breadcrumb({
          params: safeParams,
          propName,
          customerName,
          tabName,
          employeeName,
          ...customParams,
        });
      }
      if (Array.isArray(matchedRoute.breadcrumb)) {
        return matchedRoute.breadcrumb;
      }
    }

    // Fallback: Generate from path segments if no config found
    const pathSegments = location.pathname.split("/").filter(Boolean);
    let fullPath = "";
    const crumbs: BreadcrumbItem[] = [];

    const breadcrumbParams: BreadcrumbParams = {
      params: Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined),
      ) as Record<string, string>,
      propName,
      customerName,
      tabName,
      employeeName,
      ...customParams,
    };

    pathSegments.forEach((segment: string) => {
      fullPath += `/${segment}`;
      const route = routeConfig[fullPath];
      const rawIcon = route?.icon;
      const resolvedIcon =
        typeof rawIcon === "function"
          ? rawIcon(breadcrumbParams)
          : rawIcon;

      crumbs.push({
        path: fullPath,
        title: route?.title || segment.charAt(0).toUpperCase() + segment.slice(1),
        icon: resolvedIcon,
      });
    });

    return crumbs;
  }, [
    matchedRoute,
    location.pathname,
    params,
    routeConfig,
    propName,
    customerName,
    tabName,
    employeeName,
    customParams,
  ]);

  // 3. Resolve the icon (from matched route or fallback to last breadcrumb item)
  const icon = useMemo(() => {
    const raw = matchedRoute?.icon || breadcrumbItems[breadcrumbItems.length - 1]?.icon;
    if (typeof raw === "function") {
      return (raw as (p: BreadcrumbParams) => React.ReactNode)({ params, propName, employeeName, ...customParams });
    }
    return raw;
  }, [matchedRoute, breadcrumbItems, params, propName, employeeName, customParams]);

  // title が空 (データロード中など) の項目は描画しない。
  // 上位の breadcrumb 関数が `propName ?? ""` のような形で生成した未確定項目をスキップする。
  const resolvedItems = breadcrumbItems.filter(
    (item) => item.title !== "" && item.title != null,
  );

  // Apply maxItems if specified
  const displayItems =
    maxItems && resolvedItems.length > maxItems
      ? [
          resolvedItems[0],
          { path: "", title: "...", icon: null },
          ...resolvedItems.slice(-(maxItems - 2)),
        ]
      : resolvedItems;

  const handleItemClick = (item: BreadcrumbItem, index: number) => {
    if (onItemClick) {
      onItemClick(item, index);
    }
    if (onNavigate && item.path) {
      onNavigate(item.path);
    }
  };

  const styles = {
    container: {
      width: "100%",
      display: "flex",
      alignItems: "center",
      ...containerStyle,
    },
    icon: {
      width: 26,
      height: 26,
      backgroundColor: mergedTheme.iconBackgroundColor,
      border: `1px solid ${mergedTheme.iconBorderColor}`,
      color: mergedTheme.iconColor,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 2,
      mr: 1,
      ...iconStyle,
    },
    activeText: {
      color: mergedTheme.activeColor,
      fontSize: mergedTheme.fontSize,
      fontWeight: mergedTheme.fontWeight,
      display: "flex",
      width: "100%",
    },
    inactiveLink: {
      color: mergedTheme.inactiveColor,
      fontSize: mergedTheme.fontSize,
      fontWeight: mergedTheme.fontWeight,
      textDecoration: mergedTheme.linkDecoration,
    },
  };

  return (
    <Stack direction="row" sx={styles.container}>
      {showIcon && icon && (
        <Box sx={styles.icon}>{icon as React.ReactNode}</Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
        <Breadcrumbs
          aria-label="breadcrumb"
          separator={separator}
          sx={{
            width: "100%",
            "& .MuiBreadcrumbs-ol": {
              flexWrap: "nowrap",
              alignItems: "center",
              display: "flex",
              width: "100%",
            },
            "& .MuiBreadcrumbs-li": {
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
            },
            "& .MuiBreadcrumbs-li:has(.breadcrumb-truncatable)": {
              flex: "0 1 auto",
              minWidth: 0,
              maxWidth: 200,
              overflow: "hidden",
            },
            "& .MuiBreadcrumbs-li:last-child": {
              flex: "1 1 0%",
              minWidth: 0,
              overflow: "hidden",
            },
          }}
        >
          {displayItems.map((item: BreadcrumbItem, idx: number) => {
            const isLast = idx === displayItems.length - 1;
            const isEllipsis = item.title === "...";

            if (isEllipsis) {
              return (
                <Typography
                  key={idx}
                  sx={{
                    ...styles.activeText,
                    flexShrink: 0,
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  {item.title}
                </Typography>
              );
            }

            // 最後の項目（現在のページ）はクリック不可、黒で表示
            if (isLast) {
              return (
                <Typography
                  key={idx}
                  component="span"
                  sx={{
                    ...styles.activeText,
                    minWidth: 0,
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <CustomTruncateText text={item.title} />
                </Typography>
              );
            }

            // clickable が明示的に false の場合はクリック不可
            if (item.clickable === false) {
              return (
                <Typography
                  key={idx}
                  component="span"
                  className={item.truncatable ? "breadcrumb-truncatable" : undefined}
                  sx={{
                    color: mergedTheme.inactiveColor,
                    fontSize: mergedTheme.fontSize,
                    fontWeight: mergedTheme.fontWeight,
                    display: "inline-flex",
                    alignItems: "center",
                    ...(item.truncatable
                      ? { width: "100%", overflow: "hidden" }
                      : { whiteSpace: "nowrap" }),
                  }}
                >
                  {item.truncatable ? (
                    <CustomTruncateText text={item.title} />
                  ) : (
                    item.title
                  )}
                </Typography>
              );
            }

            return (
              <MuiLink
                key={idx}
                component={Link}
                to={item.path}
                className={item.truncatable ? "breadcrumb-truncatable" : undefined}
                sx={{
                  color: mergedTheme.inactiveColor,
                  fontSize: mergedTheme.fontSize,
                  fontWeight: mergedTheme.fontWeight,
                  textDecoration: "underline",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  "&:hover": {
                    color: "#0b9dbd",
                  },
                  ...(item.truncatable
                    ? { width: "100%", overflow: "hidden" }
                    : { whiteSpace: "nowrap" }),
                }}
                onClick={(e: React.MouseEvent) => {
                  if (onNavigate || onItemClick) {
                    e.preventDefault();
                    handleItemClick(item, idx);
                  }
                }}
              >
                {item.truncatable ? (
                  <CustomTruncateText text={item.title} />
                ) : (
                  item.title
                )}
              </MuiLink>
            );
          })}
        </Breadcrumbs>
      </Box>
    </Stack>
  );
};

// Export default
export { Breadcrumb as default };
